import { FormEvent, useState, useEffect } from "react";
import { Group, Label } from "@radix-ui/react-select";
import { Button } from "@/components/Button/Button";
import { TextInput } from "@/components/widget-components";
import { ImageData } from "@/requests/signup";

import { Select, SelectItem } from "./DocumentTypeSelect";
import { FileUploader } from "./FileUploader";

type DocOptionGroup = {
  groupName: string;
  options: string[];
  values: string[];
};

const supportingDocOptions: DocOptionGroup[] = [
  {
    groupName: "Name & Financial",
    options: ["Credit card statement", "Bank statement", "Mortgage statement"],
    values: ["creditCardStatement", "bankStatement", "mortgageStatement"],
  },
  {
    groupName: "Name & Address",
    options: [
      "Utility bill",
      "Phone bill",
      "Property tax statement",
      "Notice of assessment",
      "T4",
    ],
    values: ["utility", "phonebill", "propertyTax", "noa", "t4Statement"],
  },
  {
    groupName: "Name & DOB",
    options: ["Insurance document"],
    values: ["insuranceDocument"],
  },
];

type SupportingDocumentsProps = {
  onConfirm: (
    fileData: ImageData,
    accountType: string,
    accountNumber: string,
    companyName: string
  ) => void;
};

export const SupportingDocuments = ({
  onConfirm,
}: SupportingDocumentsProps) => {
  const [accountType, setAccountType] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [error, setError] = useState("");
  const [fileData, setFileData] = useState<ImageData | null>(null);

  const handleSuccess = (fileData: ImageData) => {
    setFileData(fileData);
    setError("");
  };

  const handleConfirm = (e: FormEvent) => {
    e.preventDefault();
    if (fileData) {
      onConfirm(fileData, accountType, accountNumber, companyName);
    }
  };

  // Check if form is valid and ready for submission
  const isFormValid =
    fileData && !error && accountType && accountNumber && companyName;

  // Add Enter key handler
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only proceed if Enter key is pressed and all form requirements are met
      if (e.key === "Enter" && isFormValid) {
        e.preventDefault();
        // Create a synthetic event for the form submission
        const syntheticEvent = {
          preventDefault: () => {},
        } as FormEvent;
        handleConfirm(syntheticEvent);
      }
    };

    // Add event listener
    document.addEventListener("keydown", handleKeyPress);

    // Remove event listener on cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [isFormValid, fileData, accountType, accountNumber, companyName]);

  return (
    <div className="flex overflow-y-auto">
    <form
      className="flex flex-col gap-2 "
      onSubmit={handleConfirm}
      data-testid="form-supporting-document"
    >
      {" "}
      <div data-testid="text-supporting-document-heading">
        Please upload a photo with the following:
      </div>
      <div data-testid="text-supporting-document-instruction">
        Please upload full cover page of document. Truncated or redacted
        documents will not be accepted.
      </div>
      <div className="px-2" data-testid="container-supporting-document-type">
        <Select
          onValueChange={(val) => setAccountType(val)}
          data-testid="select-supporting-document-type"
        >
          {supportingDocOptions.map((group) => (
            <Group
              key={group.groupName}
              data-testid={`group-supporting-document-${group.groupName.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Label
                className="text-xs  text-rock"
                data-testid={`label-supporting-document-${group.groupName.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {group.groupName}
              </Label>
              {group.options.map((option, index) => (
                <SelectItem
                  key={option}
                  value={group.values[index]}
                  data-testid={`item-supporting-document-${group.values[index]}`}
                >
                  {option}
                </SelectItem>
              ))}
            </Group>
          ))}
        </Select>
      </div>
      <div data-testid="container-supporting-document-company">
        <TextInput
          autoFocus
          placeholder="Company/Organization Name"
          onChange={(e) => setCompanyName(e.target.value)}
          data-testid="input-supporting-document-company"
        />
      </div>
      <div data-testid="container-supporting-document-account">
        <TextInput
          autoFocus
          placeholder="Account Number"
          onChange={(e) => setAccountNumber(e.target.value)}
          data-testid="input-supporting-document-account"
        />
      </div>
      {error && (
        <div
          className="text-rose-600"
          data-testid="error-supporting-document-message"
        >
          {error}
        </div>
      )}
      {fileData && (
        <div
          className="flex justify-center invisible md:visible"
          data-testid="container-supporting-document-preview"
        >
          <img
            src={fileData.url}
            width={75}
            data-testid="img-supporting-document-preview"
          />
        </div>
      )}
      <FileUploader
        onUploadComplete={handleSuccess}
        onError={setError}
        customButtonText=""
        showUploadIcon={true}
        data-testid="container-supporting-document"
      />
      <Button
        type="submit"
        disabled={!isFormValid}
        data-testid="btn-supporting-document-confirm"
      >
        Confirm
      </Button>
    </form>
    </div>
  );
};
